import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "./components/ThemeProvider";
import { AuthProvider } from "./context/AuthContext";
import PhoneRequiredGate from "./components/PhoneRequiredGate";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import SaraBubble from "./components/SaraBubble";
import ConnectionStatus from "./components/ConnectionStatus";
import WhatsAppFloatingButton from "./components/WhatsAppFloatingButton";
import PushNotificationManager from "./components/PushNotificationManager";
import "./App.css";
import { API_BASE_URL } from '@/utils/apiClient';

// =====================================================
// LAZY LOADING - Cada página se carga solo cuando se visita
// Esto reduce el bundle inicial de ~911kB a ~150-200kB
// =====================================================

// Páginas públicas
const LandingPage = lazy(() => import("./pages/LandingPage"));
const RouteLanding = lazy(() => import("./pages/RouteLanding"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SaraTest = lazy(() => import("./pages/SaraTest"));
const NotFound = lazy(() => import("./pages/NotFound"));
// RegisterPage removed - redirecting to simplified signup flow
// const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PhoneRegister = lazy(() => import("./pages/PhoneRegister"));
const VerifyOTP = lazy(() => import("./pages/VerifyOTP"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const ProfileSetup = lazy(() => import("./pages/ProfileSetup"));
const VerifyPhone = lazy(() => import("./pages/VerifyPhone"));
const VerifyIdentity = lazy(() => import("./pages/VerifyIdentity"));
const WelcomeScreen = lazy(() => import("./pages/WelcomeScreen"));
const SignupDriver = lazy(() => import("./pages/SignupDriver"));
const SignupPassenger = lazy(() => import("./pages/SignupPassenger"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const DataDeletionPage = lazy(() => import("./pages/DataDeletionPage"));

// Páginas de conductor
const DriverWorkDashboard = lazy(() => import("./pages/DriverWorkDashboard"));
const DriverRegistration = lazy(() => import("./pages/DriverRegistration"));
const CreateRoute = lazy(() => import("./pages/CreateRoute"));
const DriverRouteDetail = lazy(() => import("./pages/DriverRouteDetail"));
const DriverVehicle = lazy(() => import("./pages/DriverVehicle"));
const VehicleVerification = lazy(() => import("./pages/VehicleVerification"));
const DriverEarnings = lazy(() => import("./components/DriverEarnings"));

// Páginas de pasajero
const PassengerDashboard = lazy(() => import("./pages/PassengerDashboard"));
const SearchRoutes = lazy(() => import("./pages/SearchRoutes"));
const RouteDetails = lazy(() => import("./pages/RouteDetails"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const RoleSelector = lazy(() => import("./pages/RoleSelector"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const CryptoPaymentReturn = lazy(() => import("./pages/CryptoPaymentReturn"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const RateDriver = lazy(() => import("./pages/RateDriver"));
const RatePassenger = lazy(() => import("./pages/RatePassenger"));
const MyProfile = lazy(() => import("./pages/MyProfile"));

// Chat y comunicación
const ChatPage = lazy(() => import("./pages/ChatPage"));
const BookingChat = lazy(() => import("./pages/BookingChat"));
const MyBookingChats = lazy(() => import("./pages/MyBookingChats"));
const RouteInquiryChat = lazy(() => import("./pages/RouteInquiryChat"));

// Paquetes y tracking
const SendPackage = lazy(() => import("./pages/SendPackage"));
const PackageTracking = lazy(() => import("./pages/PackageTracking"));
const LiveTracking = lazy(() => import("./pages/LiveTracking"));
const TrafficAlerts = lazy(() => import("./pages/TrafficAlerts"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));

// Admin
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const SuperAdminDashboard = lazy(() => import("./pages/SuperAdminDashboard"));

// =====================================================
// LOADING SPINNER - Se muestra mientras carga cada página
// Usa los mismos estilos que tu loading actual
// =====================================================
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-white/60 text-sm">Cargando...</p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // Hide the floating WhatsApp button on auth/onboarding screens (less distracting)
  const hideWaButton = [
    '/', '/login', '/forgot-password', '/phone-register',
    '/verify-otp', '/verify-phone', '/verify-identity',
    '/complete-profile', '/profile-setup', '/welcome',
    '/signup-driver', '/signup-passenger', '/auth-callback',
    '/privacidad', '/admin',
    // Chats internos: las burbujas tapaban el botón de enviar en móvil
    '/booking-chat', '/route-inquiry', '/chat', '/my-booking-chats',
  ].some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthCallback />
      </Suspense>
    );
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/viajes/:slug" element={<RouteLanding />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Navigate to="/signup/passenger" replace />} />
        {/* INTENT-FIRST: Separate signup paths for driver and passenger */}
        <Route path="/signup/driver" element={<SignupDriver />} />
        <Route path="/signup/passenger" element={<SignupPassenger />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/phone-register" element={<PhoneRegister />} />
        <Route path="/verify-otp" element={<VerifyOTP />} />
        <Route path="/complete-profile" element={<CompleteProfile />} />
        <Route path="/profile-setup" element={<ProfileSetup />} />
        <Route path="/verify-phone" element={<VerifyPhone />} />
        <Route path="/verify-identity" element={<VerifyIdentity />} />
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/become-driver" element={
          <ProtectedRoute>
            <DriverRegistration />
          </ProtectedRoute>
        } />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/crypto/payment-return" element={<CryptoPaymentReturn />} />
        <Route path="/payment-cancel" element={<PaymentCancel />} />
        
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardRedirect />
          </ProtectedRoute>
        } />
        
        <Route path="/admin" element={
          <ProtectedRoute>
            <ErrorBoundary fallbackTitle="Algo salió mal" fallbackMessage="Ocurrió un error en el panel de administración. Intenta de nuevo.">
              <AdminPanel />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/super" element={
          <ProtectedRoute>
            <ErrorBoundary fallbackTitle="Algo salió mal" fallbackMessage="Ocurrió un error en el panel de super admin. Intenta de nuevo.">
              <SuperAdminDashboard />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        <Route path="/select-role" element={
          <ProtectedRoute>
            <RoleSelector />
          </ProtectedRoute>
        } />
        
        <Route path="/passenger/dashboard" element={
          <ProtectedRoute>
            <ErrorBoundary fallbackTitle="Algo salió mal" fallbackMessage="Ocurrió un error en tu panel. Intenta de nuevo.">
              <PassengerDashboard />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        <Route path="/driver/dashboard" element={
          <ProtectedRoute>
            <ErrorBoundary fallbackTitle="Algo salió mal" fallbackMessage="Ocurrió un error en tu panel de conductor. Intenta de nuevo.">
              <DriverWorkDashboard />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        <Route path="/driver/route/:routeId" element={
          <ProtectedRoute>
            <DriverRouteDetail />
          </ProtectedRoute>
        } />
        
        <Route path="/driver/vehicle" element={
          <ProtectedRoute>
            <DriverVehicle />
          </ProtectedRoute>
        } />
        
        
        <Route path="/search" element={
          <ProtectedRoute>
            <SearchRoutes />
          </ProtectedRoute>
        } />
        
        <Route path="/sara-test" element={
          <ProtectedRoute>
            <SaraTest />
          </ProtectedRoute>
        } />
        
        <Route path="/route/:routeId" element={
          <ProtectedRoute>
            <RouteDetails />
          </ProtectedRoute>
        } />
        
        <Route path="/my-bookings" element={
          <ProtectedRoute>
            <MyBookings />
          </ProtectedRoute>
        } />
        
        <Route path="/create-route" element={
          <ProtectedRoute>
            <CreateRoute />
          </ProtectedRoute>
        } />
        
        <Route path="/chat/:routeId" element={
          <ProtectedRoute>
            <ErrorBoundary fallbackTitle="Error en el chat" fallbackMessage="No pudimos cargar el chat. Intenta de nuevo.">
              <ChatPage />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        <Route path="/booking-chat/:bookingId" element={
          <ProtectedRoute>
            <ErrorBoundary fallbackTitle="Error en el chat" fallbackMessage="No pudimos cargar el chat de reserva.">
              <BookingChat />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        <Route path="/my-booking-chats" element={
          <ProtectedRoute>
            <MyBookingChats />
          </ProtectedRoute>
        } />
        
        <Route path="/route-inquiry/:routeId" element={
          <ProtectedRoute>
            <RouteInquiryChat />
          </ProtectedRoute>
        } />
        
        <Route path="/send-package/:routeId" element={
          <ProtectedRoute>
            <SendPackage />
          </ProtectedRoute>
        } />
        
        <Route path="/package-tracking" element={
          <PackageTracking />
        } />

        {/* Alias público para /tracking */}
        <Route path="/tracking" element={
          <PackageTracking />
        } />
        
        <Route path="/referrals" element={
          <ProtectedRoute>
            <ReferralPage />
          </ProtectedRoute>
        } />
        
        <Route path="/tracking/:routeId" element={
          <ProtectedRoute>
            <ErrorBoundary fallbackTitle="Error en el mapa" fallbackMessage="No pudimos cargar el seguimiento en vivo.">
              <LiveTracking />
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        
        <Route path="/traffic-alerts" element={
          <ProtectedRoute>
            <TrafficAlerts />
          </ProtectedRoute>
        } />
        
        <Route path="/rate-driver/:routeId" element={
          <ProtectedRoute>
            <RateDriver />
          </ProtectedRoute>
        } />
        
        <Route path="/rate-passenger/:bookingId" element={
          <ProtectedRoute>
            <RatePassenger />
          </ProtectedRoute>
        } />
        
        <Route path="/my-profile" element={
          <ProtectedRoute>
            <MyProfile />
          </ProtectedRoute>
        } />
        
        <Route path="/driver/earnings" element={
          <ProtectedRoute>
            <DriverEarnings />
          </ProtectedRoute>
        } />
        
        <Route path="/driver/vehicle-verification" element={
          <ProtectedRoute>
            <VehicleVerification />
          </ProtectedRoute>
        } />
        
        {/* Public Pages */}
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/privacidad" element={<PrivacyPage />} />
        <Route path="/eliminacion-de-datos" element={<DataDeletionPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!hideWaButton && <WhatsAppFloatingButton />}
      <SaraBubble />
    </Suspense>
  );
}

function DashboardRedirect() {
  const [loading, setLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState('/passenger/dashboard');
  
  useEffect(() => {
    const determineRedirect = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setRedirectPath('/login');
          setLoading(false);
          return;
        }
        
        // ALWAYS fetch fresh user data from server
        const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const user = response.data;
        localStorage.setItem('user', JSON.stringify(user));
        
        // =====================================================
        // INTENT-FIRST ROUTING - CENTRALIZED LOGIC
        // Priority order is CRITICAL - DO NOT CHANGE
        // =====================================================
        
        // =====================================================
        // PRIORITY 0: Admin/Super Admin always go to admin panel
        // =====================================================
        if (user.role === 'admin' || user.role === 'super_admin') {
          setRedirectPath('/admin');
          setLoading(false);
          return;
        }
        
        // =====================================================
        // PRIORITY 1: DRIVER PATH - preferred_view === 'driver'
        // THIS IS THE #1 PRIORITY FOR NON-ADMIN USERS
        // If user chose "Quiero Conducir", they MUST go to /become-driver
        // BYPASSING all profile setup forms for passengers
        // =====================================================
        if (user.preferred_view === 'driver' || user.has_driver_application) {
          // Already an approved/active driver? Go to driver dashboard
          if (user.role === 'driver' || user.driver_status === 'active') {
            setRedirectPath('/driver/dashboard');
            setLoading(false);
            return;
          }
          // Pending review? Go to driver dashboard to see status
          if (user.driver_status === 'pending_review') {
            setRedirectPath('/driver/dashboard');
            setLoading(false);
            return;
          }
          // Trampa B: intención de conductor O solicitud existente (manda sobre role/preferred_view)
          // → continuar registro. BYPASSES profile-setup completely
          setRedirectPath('/become-driver');
          setLoading(false);
          return;
        }
        
        // =====================================================
        // PRIORITY 2: NEW USER - No preferred_view? Go to role selector
        // =====================================================
        if (!user.preferred_view) {
          setRedirectPath('/select-role');
          setLoading(false);
          return;
        }
        
        // =====================================================
        // PRIORITY 3: PASSENGER PATH - preferred_view === 'passenger'
        // Only passengers need profile-setup and phone verification
        // =====================================================
        if (user.preferred_view === 'passenger') {
          // Check if basic profile is complete
          if (!user.name || !user.birth_date || !user.gender) {
            setRedirectPath('/profile-setup');
            setLoading(false);
            return;
          }
          // Teléfono obligatorio (sin OTP forzado): si falta el número, el
          // PhoneRequiredGate global lo pide en el dashboard. La verificación
          // OTP quedó como opción de login, no como requisito.
          // All complete, go to passenger dashboard
          setRedirectPath('/passenger/dashboard');
          setLoading(false);
          return;
        }
        
        // Fallback: go to role selector
        setRedirectPath('/select-role');
      } catch (error) {
        console.error('Dashboard redirect error:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setRedirectPath('/login');
      } finally {
        setLoading(false);
      }
    };
    
    determineRedirect();
  }, []);
  
  if (loading) {
    return <PageLoader />;
  }
  
  return <Navigate to={redirectPath} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <div className="App">
              <ConnectionStatus />
              <PhoneRequiredGate />
              <AppRouter />
              <PushNotificationManager />
            </div>
          </AuthProvider>
        </BrowserRouter>
        <Toaster />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
